/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../api/config";

const DEFAULT_SETTINGS = {
  max_daily_loss: "",
  risk_percent: "0.30",
  max_trade_risk: "1.00",
  max_open_positions: 3,
  enforce_cooldowns: true,
  consecutive_loss_limit: 3,
  cooldown_period: "00:30:00",
  max_lot_size: "1.00",
  max_open_trades_same_symbol: 1,
};

const REQUIRED_FIELDS = {
  max_daily_loss: "Max Daily Loss",
  risk_percent: "Default Risk",
  max_trade_risk: "Max Trade Risk",
  max_open_positions: "Max Open Positions",
  enforce_cooldowns: "Enforce Cooldowns",
  consecutive_loss_limit: "Consecutive Loss Limit",
  cooldown_period: "Cooldown Period",
  max_lot_size: "Max Lot Size",
  max_open_trades_same_symbol: "Max Open Trades (Same Symbol)",
};

const COOLDOWN_PATTERN = /^([0-1]?\d|2[0-3]):[0-5]\d:[0-5]\d$/;
const baseInputClass = "w-full border p-2 rounded bg-backgroundSurface text-textPrimary placeholder-textSecondary focus:ring-2 disabled:opacity-50";

const normalizeSettings = (data = {}) => ({
  ...data,
  max_daily_loss: data.max_daily_loss ?? DEFAULT_SETTINGS.max_daily_loss,
  risk_percent: data.risk_percent ?? DEFAULT_SETTINGS.risk_percent,
  max_trade_risk: data.max_trade_risk ?? DEFAULT_SETTINGS.max_trade_risk,
  max_open_positions: data.max_open_positions ?? DEFAULT_SETTINGS.max_open_positions,
  enforce_cooldowns: data.enforce_cooldowns ?? DEFAULT_SETTINGS.enforce_cooldowns,
  consecutive_loss_limit: data.consecutive_loss_limit ?? DEFAULT_SETTINGS.consecutive_loss_limit,
  cooldown_period: data.cooldown_period ?? DEFAULT_SETTINGS.cooldown_period,
  max_lot_size: data.max_lot_size ?? DEFAULT_SETTINGS.max_lot_size,
  max_open_trades_same_symbol:
    data.max_open_trades_same_symbol ?? DEFAULT_SETTINGS.max_open_trades_same_symbol,
});

const buildPayload = (settings) => ({
  max_daily_loss: settings.max_daily_loss,
  risk_percent: settings.risk_percent,
  max_trade_risk: settings.max_trade_risk,
  max_open_positions: settings.max_open_positions,
  enforce_cooldowns: settings.enforce_cooldowns,
  consecutive_loss_limit: settings.consecutive_loss_limit,
  cooldown_period: settings.cooldown_period,
  max_lot_size: settings.max_lot_size,
  max_open_trades_same_symbol: settings.max_open_trades_same_symbol,
});

const validateSettings = (settings) => {
  const nextErrors = {};

  Object.entries(REQUIRED_FIELDS).forEach(([field, label]) => {
    const value = settings?.[field];
    const isEmpty = typeof value === "boolean"
      ? value === null || value === undefined
      : value === null || value === undefined || String(value).trim() === "";

    if (isEmpty) {
      nextErrors[field] = `${label} is required.`;
    }
  });

  if (settings?.cooldown_period && !COOLDOWN_PATTERN.test(settings.cooldown_period)) {
    nextErrors.cooldown_period = "Enter time in HH:MM:SS format.";
  }

  return nextErrors;
};

function RiskSettings({ token, accountId, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [canUpdate, setCanUpdate] = useState(true);

  useEffect(() => {
    console.log("RiskSettings component mounted. Token:", token, "Account ID:", accountId);
    axios
      .get(`${API_BASE_URL}/api/risk/settings/${accountId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const data = normalizeSettings(response.data);
        console.log("Risk settings response:", data);
        setSettings(data);
        setFieldErrors({});
        setError("");
        setLoading(false);
        if (data.last_updated) {
          console.log("Raw last_updated field:", data.last_updated);
          const lastUpdate = new Date(data.last_updated);
          const now = new Date();
          const diffDays = (now - lastUpdate) / (1000 * 3600 * 24);
          console.log("Computed diffDays:", diffDays);
          // Adjust threshold as needed.
          if (diffDays < 7) {
            console.log("Settings updated too recently. Disabling update.");
            setCanUpdate(false);
          } else {
            console.log("Settings last updated long enough. Enabling update.");
            setCanUpdate(true);
          }
        } else {
          console.log("No last_updated field found in risk settings response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching risk settings:", error);
        setError("Failed to fetch risk settings");
        setLoading(false);
      });
  }, [token, accountId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`Field change [${name}]:`, type === "checkbox" ? checked : value);
    setError("");
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[name];
      return nextErrors;
    });
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    const nextFieldErrors = validateSettings(settings);
    setFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      setError("Please fill in the highlighted fields.");
      return;
    }

    const payload = buildPayload(settings);
    console.log("Submitting risk settings with payload:", payload);
    axios
      .put(`${API_BASE_URL}/api/risk/settings/${accountId}/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("Risk settings updated response:", res.data);
        const nextSettings = normalizeSettings(res.data);
        setSettings(nextSettings);
        setFieldErrors({});
        if (onSaved) {
          onSaved(nextSettings);
        }
      })
      .catch((err) => {
        console.error("Failed to update risk settings:", err);
        const responseData = err.response?.data;
        if (responseData && typeof responseData === "object") {
          const nextServerErrors = Object.entries(responseData).reduce((acc, [field, errors]) => {
            if (field === "detail") return acc;

            acc[field] = Array.isArray(errors) ? errors.join(" ") : String(errors);
            return acc;
          }, {});

          setFieldErrors(nextServerErrors);
          setError(responseData.detail || "Please review the highlighted fields.");
        } else {
          setError("Failed to update risk settings");
        }
      });
  };

  const getInputClassName = (fieldName) => `${baseInputClass} ${fieldErrors[fieldName]
    ? "border-destructive focus:ring-destructive focus:border-destructive"
    : "border-borderDefault focus:ring-primary focus:border-primary"}`;

  const renderTextInput = ({ label, name, type = "number", step, placeholder, pattern, title }) => (
    <div>
      <label className="block font-medium text-textPrimary mb-1">
        {label}
      </label>
      <input
        type={type}
        name={name}
        step={step}
        value={settings[name] ?? ""}
        onChange={handleChange}
        disabled={!canUpdate}
        placeholder={placeholder}
        pattern={pattern}
        title={title}
        className={getInputClassName(name)}
      />
      {fieldErrors[name] && (
        <p className="text-destructive text-xs mt-1">{fieldErrors[name]}</p>
      )}
    </div>
  );

  if (loading) return <div className="text-textSecondary p-4">Loading risk settings...</div>; {/* Updated text color */}
  if (!settings) return <div className="text-destructive p-4">{error}</div>;

  return (
    <div className="bg-backgroundSurface p-4 rounded shadow"> {/* Updated background */}
      <h4 className="text-lg font-semibold text-textPrimary mb-3">Risk Settings</h4> {/* Updated text color */}
      {!canUpdate && (
        <div className="bg-warning/20 text-warning-dark p-2 mb-3 rounded"> {/* Updated warning box colors (assuming warning-dark for text on light warning bg) */}
          Risk settings can only be updated once a week. Current settings are read-only.
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderTextInput({ label: "Max Daily Loss (%)", name: "max_daily_loss" })}
          {renderTextInput({ label: "Default Risk (%)", name: "risk_percent", step: "0.01" })}
          {renderTextInput({ label: "Max Trade Risk (%)", name: "max_trade_risk" })}
          {renderTextInput({ label: "Max Open Positions", name: "max_open_positions" })}
          {renderTextInput({ label: "Consecutive Loss Limit", name: "consecutive_loss_limit" })}
          {renderTextInput({
            label: "Cooldown Period (HH:MM:SS)",
            name: "cooldown_period",
            type: "text",
            placeholder: "00:30:00",
            pattern: "^([0-1]?\\d|2[0-3]):[0-5]\\d:[0-5]\\d$",
            title: "Enter time in HH:MM:SS format",
          })}
          {renderTextInput({ label: "Max Lot Size", name: "max_lot_size" })}
          {renderTextInput({ label: "Max Open Trades (Same Symbol)", name: "max_open_trades_same_symbol" })}
          <div>
            <label className="block font-medium text-textPrimary mb-1">
              Enforce Cooldowns
            </label>
            <input
              type="checkbox"
              name="enforce_cooldowns"
              checked={settings.enforce_cooldowns ?? false}
              onChange={handleChange}
              disabled={!canUpdate}
              className={`w-6 h-6 text-primary rounded focus:ring-2 disabled:opacity-50 ${fieldErrors.enforce_cooldowns
                ? "border-destructive focus:ring-destructive"
                : "border-borderDefault focus:ring-primary"}`}
            />
            {fieldErrors.enforce_cooldowns && (
              <p className="text-destructive text-xs mt-1">{fieldErrors.enforce_cooldowns}</p>
            )}
          </div>
        </div>
        <button
          type="submit"
          disabled={!canUpdate}
          className="btn btn-primary w-full mt-4 disabled:opacity-50" /* Updated to use btn and btn-primary */
        >
          Save Settings
        </button>
        {error && (
          <div className="text-destructive mt-3 whitespace-pre-line text-sm">{error}</div>
        )}
      </form>
    </div>
  );
}

export default RiskSettings;
